import {
  createElement,
  Fragment,
  useCallback,
  useMemo,
  useState,
  type CSSProperties,
} from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import {
  formatMarketingAmount,
  formatStatusPercent,
  toMarketingExpenseView,
  type MarketingAmount,
  type MarketingAnalysis,
  type MarketingExpenseView,
} from '@/utils/approval/marketingExpense';
import { getBrandIconHtml } from './brandIcons';
import { MARKETING_EXPENSE_LEGACY_CSS } from './legacyCss';
import { MarketingExpenseRoot } from './styles';

type ToggleState = {
  showDetail: boolean;
  /** 예산 확인 표 펼침 */
  expanded: boolean;
};

const INITIAL_TOGGLE: ToggleState = { showDetail: false, expanded: false };

type ReportProps = {
  view: MarketingExpenseView | null;
  t: TFunction;
  toggle?: ToggleState;
  onToggleDetail?: () => void;
  onToggleCollapse?: () => void;
};

const HIDDEN: CSSProperties = { display: 'none' };

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const clampPercent = (v: number) => Math.max(0, Math.min(100, v));

const getCategoryLabel = (category: string, t: TFunction) => {
  switch (category) {
    case 'common':
      return t('공통');
    case 'category':
      return t('카테고리');
    case 'etc':
      return t('기타');
    default:
      return category;
  }
};

export const getSummaryTitle = (showDetail: boolean, t: TFunction) =>
  showDetail ? t('상세 분석') : t('3줄 요약');

export const getSummaryButtonText = (showDetail: boolean, t: TFunction) =>
  showDetail ? t('3줄 요약 보기') : t('상세 분석 보기');

const SwapIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M6.875 8.75L5 6.875M5 6.875L6.875 5M5 6.875H15M13.125 11.25L15 13.125M15 13.125L13.125 15M15 13.125H5"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CollapseIcon = () => (
  <svg width="28" height="28" viewBox="0 0 32 33" fill="none">
    <path
      d="M8 13.5391L16 21.5391L24 13.5391"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** 레거시 #apv_sub_mkt_content 마크업(id·class 동일) */
const MarketingExpenseReport = ({
  view,
  t,
  toggle = INITIAL_TOGGLE,
  onToggleDetail,
  onToggleCollapse,
}: ReportProps) => {
  const won = (n: MarketingAmount) =>
    t('{{amount}}원', { amount: formatMarketingAmount(n) });
  const brandLabel = view?.brand === 'ALL' ? t('통합') : view?.brand ?? '';
  const season = view?.season ?? '';
  const { showDetail, expanded } = toggle;
  const statusRows = view
    ? [
        { key: 'used', item: view.status.used, label: t('사용된 예산') },
        {
          key: 'current',
          item: view.status.current,
          label: t('금번 사용 예정'),
        },
        { key: 'remaining', item: view.status.remain, label: t('잔여 예산') },
      ]
    : [];

  return (
    <div className="apv_sub_mkt_case pt-10 pb-10">
      <div className="header mt-10">{t('AI 분석 요약')}</div>
      <div className="container mt-10">
        <div className="summary-section mb-20">
          <div className="summary-header">
            <div className="summary-title" id="summaryTitle">
              {getSummaryTitle(showDetail, t)}
            </div>
            <button
              type="button"
              className="detail-button summary"
              id="detailButton"
              onClick={onToggleDetail}
            >
              <div className="btn-content">
                <span id="buttonText">
                  {getSummaryButtonText(showDetail, t)}
                </span>
                <SwapIcon />
              </div>
            </button>
          </div>

          <div className="summary-wrap">
            <ul
              className="summary-list"
              id="summaryListBrief"
              style={showDetail ? HIDDEN : undefined}
            >
              {view?.brief?.map((segments, i) => (
                <li key={i}>
                  {segments.map((s, si) =>
                    s.bold ? (
                      <strong key={si}>{s.text}</strong>
                    ) : (
                      <Fragment key={si}>{s.text}</Fragment>
                    ),
                  )}
                </li>
              ))}
            </ul>
            <div
              className="summary-list"
              id="summaryListDetail"
              style={showDetail ? undefined : HIDDEN}
              // 마케팅 분석 화면이 만든 HTML(renderAiCostSummaryHTML) — 레거시도 .html() 로 넣음
              dangerouslySetInnerHTML={{ __html: view?.detailHtml ?? '' }}
            />
          </div>
        </div>

        <div className="detail-section">
          <div className="section-title">{t('개요')}</div>
          <div className="info-grid">
            <div className="info-item">
              <div className="info-label">{t('브랜드')}</div>
              <div
                className="info-value brand-info"
                dangerouslySetInnerHTML={{
                  __html: `${getBrandIconHtml(
                    view?.brand ?? '',
                  )}<span class="value_brand">${escapeHtml(brandLabel)}</span>`,
                }}
              />
            </div>
            <div className="info-item">
              <div className="info-label">{t('예산 시즌')}</div>
              <div className="info-value value_season">{season}</div>
            </div>
            <div className="info-item">
              <div className="info-label">{t('연령대')}</div>
              <div className="info-value" id="value_brandCode">
                {view ? (view.isKids ? t('아동') : t('성인')) : ''}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">{t('마케팅 실행 기간')}</div>
              <div className="info-value">
                <span id="value_dateRange_0">{view?.startDate}</span> ~{' '}
                <span id="value_dateRange_1">{view?.endDate}</span>
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">{t('비용/구분')}</div>
              <div className="info-value">
                <span id="value_category">
                  {view ? getCategoryLabel(view.category, t) : ''}
                </span>
                <span id="value_categoryItemCodes">
                  {view?.categoryItemCodes.length
                    ? ` - ${view.categoryItemCodes.join(', ')}`
                    : ''}
                </span>
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">{t('목적')}</div>
              <div className="info-value" id="value_purpose">
                {view?.purpose ?? t('(기안 목적 미기재)')}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">
                {t('내용', { context: 'detail' })}
              </div>
              <div className="info-value content" id="value_summary">
                {view?.summary}
              </div>
            </div>
          </div>

          <hr className="divider" />

          <div className="budget-summary">
            <div className="section-title">{t('사용 예정 내역')}</div>
            <div className="total-amount">
              <span>{t('총')}</span>{' '}
              <span className="amount" id="totalCost">
                {view ? formatMarketingAmount(view.totalCost) : ''}
              </span>{' '}
              <span>{t('원')}</span>
            </div>
          </div>

          <div className="table-container">
            <table className="budget-table table table-fixed">
              <tbody>
                {view?.groups.map((group, gi) => (
                  <Fragment key={`${group.name}-${gi}`}>
                    <tr className="category-header">
                      <td>
                        <span style={{ color: 'var(--dk-accent, #0066cc)' }}>
                          {gi + 1}
                        </span>
                      </td>
                      <td colSpan={3}>{group.name}</td>
                      <td className="amount-cell">{won(group.total)}</td>
                    </tr>
                    {group.rows.map((row, ri) => (
                      <tr key={ri}>
                        <td>{ri + 1}</td>
                        <td>{row.account}</td>
                        <td>{row.company}</td>
                        <td>{row.note}</td>
                        <td className="amount-cell">{won(row.cost)}</td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <hr className="divider" />

          <div className="section-title">
            <span className="value_season">{season}</span> {t('예산 현황')}
          </div>
          <div className="progress-bar-container">
            <div className="progress-fill base">
              <div
                className="progress-fill dark"
                style={{
                  width: `${clampPercent(view?.status.used.percent ?? 0)}%`,
                }}
              />
              <div
                className="progress-fill light"
                style={{
                  width: `${clampPercent(view?.status.current.percent ?? 0)}%`,
                }}
              />
            </div>
          </div>

          <div className="budget-status-wrap">
            {statusRows.map(({ key, item, label }) => (
              <div className="budget-status" key={key}>
                <div className="status-info">
                  <div className={`status-dot status-${key}`} />
                  <span>
                    <strong className="percent-range">
                      {formatStatusPercent(item.percent)}
                    </strong>{' '}
                    {label}
                  </span>
                </div>
                <div className="budget">
                  {formatMarketingAmount(item.amount)}
                </div>
              </div>
            ))}
          </div>

          <div className="budget-status-total mb-10">
            <strong>{t('금번 품의 사용 예정')}</strong>
            <div className="sap-list">
              {view?.sapUsages.map((usage, i) => (
                <div className="budget-status" key={i}>
                  <div>{usage.name}</div>
                  <div className="d-flex flex-wrap justify-content-end">
                    <div>
                      <span style={{ fontWeight: 500 }}>
                        {formatMarketingAmount(usage.curr)}
                      </span>
                      <span
                        style={{
                          color: 'var(--dk-text-secondary, #888)',
                          marginLeft: 4,
                        }}
                      >
                        / {won(usage.total)}
                      </span>
                    </div>
                    <div>
                      <span style={{ fontWeight: 500, marginLeft: 16 }}>
                        {t('{{percent}} 사용', { percent: usage.percentText })}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <hr className="divider" />

          <div className="summary-header">
            <div className="section-title">
              <span className="value_brand">{brandLabel}</span>{' '}
              <span className="value_season">{season}</span> {t('예산 확인')}
            </div>
            {/* 레거시는 접힌 상태에 open 클래스 — 아이콘 방향 그대로 */}
            <button
              type="button"
              className={expanded ? 'collapse-button' : 'collapse-button open'}
              id="btnCollapse"
              onClick={onToggleCollapse}
            >
              <CollapseIcon />
            </button>
          </div>

          <div
            className="table-container table-season-sortable collapse-content"
            style={expanded ? undefined : HIDDEN}
          >
            <table className="sortable-table table-fixed">
              <thead>
                <tr>
                  <th className="text-center">{t('구분')}</th>
                  <th className="text-center">{t('전체 예산')}</th>
                  <th className="text-center">{t('잔여 예산')}</th>
                  <th className="text-center">{t('금번품의 사용 예산')}</th>
                  <th className="text-center">{t('집행률(집행시)')}</th>
                </tr>
              </thead>
              <tbody>
                {view?.budgetChecks.map((row, i) => (
                  <tr key={i}>
                    <td>{row.type}</td>
                    <td className="text-right">
                      {formatMarketingAmount(row.budgetTotal)}
                    </td>
                    <td className="text-right">
                      {formatMarketingAmount(row.remain)}
                    </td>
                    <td className="text-right">
                      {formatMarketingAmount(row.currentUsage)}
                    </td>
                    <td>
                      <div className="execution-rate">
                        <div className="rate-bar">
                          <div
                            className="rate-fill"
                            style={{
                              width: `${Math.min(row.proceedPercent, 100)}%`,
                            }}
                          />
                        </div>
                        <div className="rate-text">{row.proceedPercent}%</div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const useToggleState = (initial: ToggleState) => {
  const [toggle, setToggle] = useState(initial);
  const onToggleDetail = useCallback(
    () => setToggle((s) => ({ ...s, showDetail: !s.showDetail })),
    [],
  );
  const onToggleCollapse = useCallback(
    () => setToggle((s) => ({ ...s, expanded: !s.expanded })),
    [],
  );
  return { toggle, onToggleDetail, onToggleCollapse };
};

/** 작성 화면 — 분석 레코드로 그림. 요약/상세·예산 확인 접기는 화면에서만 바뀌고 저장 본문은 처음 상태 */
const MarketingExpenseInfo = ({
  analysis,
}: {
  analysis: MarketingAnalysis | null;
}) => {
  const { t } = useTranslation();
  const view = useMemo(() => toMarketingExpenseView(analysis), [analysis]);
  const { toggle, onToggleDetail, onToggleCollapse } =
    useToggleState(INITIAL_TOGGLE);
  return (
    <MarketingExpenseRoot>
      <MarketingExpenseReport
        view={view}
        t={t}
        toggle={toggle}
        onToggleDetail={onToggleDetail}
        onToggleCollapse={onToggleCollapse}
      />
    </MarketingExpenseRoot>
  );
};

const DROP_TAGS = new Set(['style', 'script', 'link']);

const isHiddenStyle = (el: Element | null | undefined) =>
  (el as HTMLElement | null | undefined)?.style?.display === 'none';

/** 저장 HTML 의 처음 토글 상태 — 레거시는 저장 순간 화면 상태 그대로 실림 */
const readSnapshotToggle = (content: string): ToggleState => {
  if (typeof window === 'undefined') return INITIAL_TOGGLE;
  const doc = new DOMParser().parseFromString(content, 'text/html');
  return {
    showDetail: isHiddenStyle(doc.getElementById('summaryListBrief')),
    expanded:
      !!doc.querySelector('.table-season-sortable') &&
      !isHiddenStyle(doc.querySelector('.table-season-sortable')),
  };
};

const SVG_NS = 'http://www.w3.org/2000/svg';

/** 저장 HTML 의 SVG 속성(stroke-linecap 등)을 React 이름으로 — 그대로 넘기면 경고 */
const toSvgProps = (props: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(props).map(([name, value]) => [
      /^(data|aria)-/.test(name)
        ? name
        : name.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase()),
      value,
    ]),
  );

const withDisplay = (props: Record<string, unknown>, visible: boolean) => {
  const style = { ...((props.style as CSSProperties | undefined) ?? {}) };
  if (visible) delete style.display;
  else style.display = 'none';
  return { ...props, style };
};

/**
 * 조회 화면 — 결재된 내용 그대로 저장 content(레거시 문서 포함)로 그림.
 * 요약/상세·예산 확인 접기 버튼만 살리고(레거시 조회 스크립트), 맨 아래 내부 오더를 붙임
 */
export const MarketingExpenseSnapshot = ({
  content,
  internalOrderNumber,
}: {
  content: string;
  internalOrderNumber: string;
}) => {
  const { t } = useTranslation();
  const initial = useMemo(() => readSnapshotToggle(content), [content]);
  const [override, setOverride] = useState<ToggleState | null>(null);
  const toggle = override ?? initial;

  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props, children }) => {
      if (DROP_TAGS.has(tagName)) return null;
      if (element.namespaceURI === SVG_NS) {
        return createElement(tagName, toSvgProps(props), ...children);
      }
      const key = props.key as string;
      switch (element.id) {
        case 'summaryTitle':
          return override
            ? createElement(
                tagName,
                props,
                getSummaryTitle(toggle.showDetail, t),
              )
            : undefined;
        case 'buttonText':
          return override
            ? createElement(
                tagName,
                props,
                getSummaryButtonText(toggle.showDetail, t),
              )
            : undefined;
        case 'detailButton':
          return createElement(
            tagName,
            {
              ...props,
              type: 'button',
              onClick: () =>
                setOverride({ ...toggle, showDetail: !toggle.showDetail }),
            },
            ...children,
          );
        case 'summaryListBrief':
          return createElement(
            tagName,
            withDisplay(props, !toggle.showDetail),
            ...children,
          );
        case 'summaryListDetail':
          return createElement(
            tagName,
            withDisplay(props, toggle.showDetail),
            ...children,
          );
        case 'btnCollapse':
          return createElement(
            tagName,
            {
              ...props,
              type: 'button',
              className: toggle.expanded
                ? 'collapse-button'
                : 'collapse-button open',
              onClick: () =>
                setOverride({ ...toggle, expanded: !toggle.expanded }),
            },
            ...children,
          );
        default:
          break;
      }
      if (element.classList.contains('table-season-sortable')) {
        return createElement(
          tagName,
          withDisplay(props, toggle.expanded),
          ...children,
        );
      }
      if (element.classList.contains('detail-section')) {
        return createElement(
          tagName,
          props,
          ...children,
          <div key={`${key}-internal-order`} className="mt-20">
            <div className="section-title">{t('내부 오더')}</div>
            <div className="mt-5">
              {internalOrderNumber || t('결재 완료 시 내부 오더가 생성됩니다.')}
            </div>
          </div>,
        );
      }
      return undefined;
    },
    [internalOrderNumber, override, t, toggle],
  );

  const rendered = useHtmlToReact(content, { transform });
  return <MarketingExpenseRoot>{rendered}</MarketingExpenseRoot>;
};

/**
 * attributes.content — 레거시 #apv_sub_mkt_content.innerHTML 과 같은 모양(<style> 포함).
 * 레거시 조회 화면은 이 HTML 만 그대로 출력
 */
export const buildMarketingExpenseContentHtml = (
  analysis: MarketingAnalysis | null,
  t: TFunction,
): string => {
  const view = toMarketingExpenseView(analysis);
  if (!view) return '';
  return `<style>${MARKETING_EXPENSE_LEGACY_CSS}</style>${renderToStaticMarkup(
    <MarketingExpenseReport view={view} t={t} />,
  )}`;
};

export default MarketingExpenseInfo;
